import React, { useState, useRef, useEffect, useCallback } from 'react';
import { clustersAPI } from '../services/api';
import './WebTerminal.css';

const WELCOME = `SarvKube Web Terminal
Connected to cluster via k8s-master agent.
Only kubectl commands are allowed.
Type 'help' for examples.
─────────────────────────────────────────────\n`;

const HELP_TEXT = `Examples:
  kubectl get pods -A
  kubectl get pods -n <namespace>
  kubectl describe pod <name> -n <namespace>
  kubectl logs <pod> -n <namespace> --tail=50
  kubectl get nodes -o wide
  kubectl get events -n <namespace> --sort-by=.lastTimestamp
  kubectl top nodes
  kubectl top pods -A
─────────────────────────────────────────────\n`;

export default function WebTerminal({ clusterId, onClose }) {
  const [history, setHistory]     = useState([{ type: 'system', text: WELCOME }]);
  const [input, setInput]         = useState('');
  const [running, setRunning]     = useState(false);
  const [cmdHistory, setCmdHistory] = useState([]);
  const [cmdIdx, setCmdIdx]       = useState(-1);
  const bottomRef                 = useRef(null);
  const inputRef                  = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const addLine = useCallback((type, text) => {
    setHistory(h => [...h, { type, text }]);
  }, []);

  const runCommand = useCallback(async (cmd) => {
    const trimmed = cmd.trim();
    if (!trimmed) return;

    // Local commands
    if (trimmed === 'clear') { setHistory([{ type: 'system', text: WELCOME }]); return; }
    if (trimmed === 'help')  { addLine('system', HELP_TEXT); return; }

    // Save to command history
    setCmdHistory(h => [trimmed, ...h.slice(0, 49)]);
    setCmdIdx(-1);

    addLine('input', `$ ${trimmed}`);
    setRunning(true);

    try {
      const { data } = await clustersAPI.exec(clusterId, trimmed);
      const out = (data.stdout || '').trimEnd();
      const err = (data.stderr || '').trimEnd();
      if (out) addLine('output', out);
      if (err) addLine('error', err);
      if (!out && !err) addLine('system', '(no output)');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Command failed';
      addLine('error', `Error: ${msg}`);
    } finally {
      setRunning(false);
      inputRef.current?.focus();
    }
  }, [clusterId, addLine]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !running) {
      const cmd = input;
      setInput('');
      runCommand(cmd);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(cmdIdx + 1, cmdHistory.length - 1);
      setCmdIdx(next);
      if (cmdHistory[next] !== undefined) setInput(cmdHistory[next]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(cmdIdx - 1, -1);
      setCmdIdx(next);
      setInput(next === -1 ? '' : (cmdHistory[next] || ''));
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setHistory([{ type: 'system', text: WELCOME }]);
    }
  };

  return (
    <div className="terminal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="terminal-window">
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="dot red" onClick={onClose} />
            <span className="dot yellow" />
            <span className="dot green" />
          </div>
          <span className="terminal-title">kubectl — cluster {clusterId}</span>
          <span />
        </div>

        <div className="terminal-body" onClick={() => inputRef.current?.focus()}>
          {history.map((line, i) => (
            <div key={i} className={`terminal-line terminal-${line.type}`}>
              <pre>{line.text}</pre>
            </div>
          ))}
          <div className="terminal-input-row">
            <span className="terminal-prompt">{running ? '⟳' : '$'}</span>
            <input
              ref={inputRef}
              className="terminal-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={running}
              autoComplete="off"
              spellCheck={false}
              placeholder={running ? 'running...' : ''}
            />
          </div>
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
}
